import { isTauri } from '@tauri-apps/api/core'
import { listen } from '@tauri-apps/api/event'

import { Result, Array, pipe, Data, Url } from '@blade/std-lib'
import { PlatformUtil } from '@root/shared'

import { InviteCode } from '../referral'

import * as Deeplink from './deeplink'

const GOTO_URL_EVENT = 'wallet-tracker://goto-url'

declare global {
  interface Window {
    __GOTO_NFT__: () => void
    __HANDLE_DEEPLINK: (url: string) => void
    __HANDLE_DEEPLINK_REFERRAL__: (code?: string) => void
    __OPEN_SETTINGS__?: () => void
  }
}

export class DeeplinkHandler {
  private _state: 'stopped' | 'running' = 'stopped'
  private _finalizer = () => {}

  constructor(private handler: (deeplink: Deeplink.Deeplink) => Promise<void>) {
    window.__HANDLE_DEEPLINK = (dl: string) => {
      this.handleURLs([dl])
    }
    window.__GOTO_NFT__ = () => this.handler(new Deeplink.Nft())
    window.__HANDLE_DEEPLINK_REFERRAL__ = (code?: string) =>
      this.handler(
        new Deeplink.InviteCodeJoin({
          inviteCode: InviteCode(code ?? 'test_code'),
        }),
      )
  }

  getState() {
    return this._state
  }

  private setState(state: 'stopped' | 'running') {
    this._state = state
  }

  private async handleURLs(urls_: Array<string>) {
    const urls = pipe(
      urls_,
      Array.map(Url.fromString),
      Array.filter(Result.isSuccess),
    )
    if (!Array.isNotEmpty(urls)) {
      return
    }

    const { success: url } = urls[0]

    const navigateURL = Deeplink.fromURL(url)

    // eslint-disable-next-line no-console
    console.log('[DeeplinkHandler]', {
      url: urls_.length === 1 ? urls_[0] : urls_,
      deeplink: navigateURL,
    })

    if (navigateURL) {
      await this.handler(navigateURL)
    }
  }

  async run() {
    this.setState('running')

    const urls = await getCurrentResult()

    if (this.getState() === 'stopped') {
      return
    }

    if (Result.isSuccess(urls) && urls.success) {
      await this.handleURLs(urls.success)
    }

    if (this.getState() === 'stopped') {
      return
    }

    const unlistenDeeplink = await PlatformUtil.deeplink.onOpenUrl((urls) => {
      // eslint-disable-next-line no-console
      console.log('[TOAST_DEBUG] deeplink onOpenUrl received', urls)
      if (this.getState() === 'stopped') {
        return
      }

      return this.handleURLs(urls)
    })

    let unlistenGotoUrl: (() => void) | undefined
    if (isTauri()) {
      unlistenGotoUrl = await listen<string>(GOTO_URL_EVENT, (event) => {
        // eslint-disable-next-line no-console
        console.log(
          '[TOAST_DEBUG] wallet-tracker://goto-url event received',
          event.payload,
        )
        if (this.getState() === 'stopped') {
          return
        }
        const url = event.payload
        if (url && typeof url === 'string') {
          this.handleURLs([url])
        }
      })
    }

    if (this.getState() === 'running') {
      this._finalizer = () => {
        unlistenDeeplink()
        unlistenGotoUrl?.()
      }
    } else {
      unlistenDeeplink()
      unlistenGotoUrl?.()
    }

    return this._finalizer
  }

  stop() {
    this.setState('stopped')
    this._finalizer()
    this._finalizer = () => {}
  }
}

async function getCurrentResult() {
  try {
    const urls = await PlatformUtil.deeplink.getCurrent()

    return Result.succeed(urls)
  } catch (error) {
    return Result.fail(
      new DeeplinkServiceError({
        method: 'getCurrent',
        cause: error,
      }),
    )
  }
}

export class DeeplinkServiceError extends Data.TaggedError(
  'DeeplinkServiceError',
)<{
  method: string
  cause: unknown
}> {}
